import {
  ReferenceImageSemantics,
  ModelFamily,
  ModelSurface,
  ModelTopology,
  ReferenceTransportStrategy,
} from './schema';
import { VIDEO_REFERENCE_ROLE_VALUES } from '../mediaReferenceRoles.js';
import {
  SEEDANCE_DRAFT_ENDPOINT,
  SEEDANCE_REFERENCE_ENDPOINT,
  SEEDANCE_REFERENCE_VIDEO_CONSTRAINTS,
} from '../seedanceReferenceVideo.js';
import { MEDIA_PROMPT_TAG_FORMATS } from '../../utils/mediaPromptTag.js';
import {
  GEMINI_OMNI_FLASH_ENDPOINTS,
  KLING_O1_EDIT_ENDPOINTS,
  LTX_ENDPOINTS,
  MINIMAX_H3_MAX_SPECIAL_ENDPOINTS,
} from './falEndpoints.js';
import { SCENE_ASSET_REFERENCE_BUCKET_KINDS } from '../../types/scene-assets/sceneAssetKinds';

const DEFAULT_CHECKED_AT = '2026-04-25';

const VIDEO_REFERENCE_ROLES = ['subject', 'scene', 'style'];
const VIDEO_ASPECT_RATIOS = ['16:9', '9:16', '1:1'];
// Generic placeholder menu for models whose real duration capability has not been
// curated. Exported so the cost-safety guard in videoModels.test.js can tell a
// curated menu from this one — see `videoFeatures`.
export const PLACEHOLDER_SEQUENCE_DURATIONS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const SEQUENCE_DURATIONS = PLACEHOLDER_SEQUENCE_DURATIONS;

const noReferences = {
  acceptedKinds: [],
  acceptedMediaTypes: [],
  roles: [],
  chips: [],
  limits: { total: 0 },
  transport: { strategy: ReferenceTransportStrategy.NONE },
  unsupportedBehavior: 'text-only',
};

const sourceImageReferences = ({
  lastFrameField = null,
  sourceField = 'image',
  total = 1,
} = {}) => ({
  acceptedKinds: ['source', ...(lastFrameField ? ['last-frame'] : [])],
  acceptedMediaTypes: ['image'],
  roles: ['source', ...(lastFrameField ? ['last-frame'] : [])],
  chips: ['source', ...(lastFrameField ? ['last-frame'] : [])],
  limits: {
    total,
    source: 1,
    ...(lastFrameField ? { 'last-frame': 1 } : {}),
  },
  transport: {
    strategy: ReferenceTransportStrategy.NATIVE_SLOTS,
    fields: {
      source: sourceField,
      ...(lastFrameField ? { lastFrame: lastFrameField } : {}),
    },
  },
  unsupportedBehavior: 'block',
});

const orderedImageReferences = ({
  total = 10,
  nativeElementsMax = 0,
  strategy = ReferenceTransportStrategy.ORDERED_BUCKETS,
} = {}) => ({
  acceptedKinds: ['source', 'last-frame', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS],
  acceptedMediaTypes: ['image'],
  roles: ['source', 'last-frame', ...VIDEO_REFERENCE_ROLES],
  chips: ['source', 'last-frame', ...VIDEO_REFERENCE_ROLES],
  limits: {
    total,
    source: 1,
    'last-frame': 1,
    character: 4,
    prop: 3,
    brand: 1,
    environment: 2,
    style: 1,
    ...(nativeElementsMax ? { nativeElements: nativeElementsMax } : {}),
  },
  transport: {
    strategy,
    fields: { images: 'imageUrls' },
    buckets: {
      character: { max: 4, order: 0 },
      prop: { max: 3, order: 1 },
      brand: { max: 1, order: 1.5 },
      environment: { max: 2, order: 2 },
      style: { max: 1, order: 3 },
    },
  },
  unsupportedBehavior: 'warn',
});

const makeSeedanceReferenceVideoReferences = (endpoint) => ({
  acceptedKinds: ['source-video', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS, 'reference-audio'],
  acceptedMediaTypes: ['video', 'image', 'audio'],
  roles: [...VIDEO_REFERENCE_ROLE_VALUES, 'audio-reference'],
  // rolesByMedia is derived from `transport.promptTagFormat` in schema.js.
  chips: ['source-video', 'reference', 'audio-reference'],
  limits: endpoint.limits,
  constraints: {
    image: {
      perItemBytesMax: 30_000_000,
    },
    video: {
      combinedDurationSeconds: { min: 2, max: endpoint.duration.max },
      totalBytesExclusiveMax: 50_000_000,
      ...SEEDANCE_REFERENCE_VIDEO_CONSTRAINTS,
    },
    audio: {
      combinedDurationSeconds: { max: endpoint.duration.max },
      perItemBytesMax: 15_000_000,
      requiresVisualAnchor: true,
    },
  },
  transport: {
    strategy: ReferenceTransportStrategy.PROMPT_TAGS,
    promptTagFormat: MEDIA_PROMPT_TAG_FORMATS.AT_PREFIXED,
    followSourceAspectRatio: 'auto',
    fields: {
      videos: 'referenceVideos',
      images: 'referenceImages',
      audio: 'referenceAudios',
    },
  },
  unsupportedBehavior: 'warn',
});

const seedance25ReferenceVideoReferences = makeSeedanceReferenceVideoReferences(
  SEEDANCE_REFERENCE_ENDPOINT,
);
const seedanceDraftReferenceVideoReferences = makeSeedanceReferenceVideoReferences(
  SEEDANCE_DRAFT_ENDPOINT,
);

// MiniMax H3 reads images, clips and audio in one context and addresses them
// positionally. It publishes no per-media count split and no byte or format
// caps — one combined budget of 12 files — so it gets its own contract rather
// than a narrowed copy of Seedance's.
//
// The tag format is the load-bearing difference: H3 documents `Image 1` where
// Seedance documents `@Image1`. Declaring it here is what makes the transport
// send the right one; see utils/mediaPromptTag.js.
const h3ReferenceVideoReferences = {
  acceptedKinds: ['source-video', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS, 'reference-audio'],
  acceptedMediaTypes: ['video', 'image', 'audio'],
  roles: [...VIDEO_REFERENCE_ROLE_VALUES, 'audio-reference'],
  // rolesByMedia is derived from the transport in schema.js.
  chips: ['source-video', 'reference', 'audio-reference'],
  // Per-type caps come from the endpoint schema, not from the combined total:
  // 9 images, 3 videos, 3 audio, and at most 12 files together. Declaring 12 for
  // each let the UI offer twelve image slots on a route that rejects ten.
  limits: {
    total: 12,
    video: 3,
    image: 9,
    audio: 3,
  },
  constraints: {
    video: {
      combinedDurationSeconds: { min: 2, max: 15 },
    },
    audio: {
      combinedDurationSeconds: { max: 15 },
      requiresVisualAnchor: true,
    },
  },
  transport: {
    strategy: ReferenceTransportStrategy.PROMPT_TAGS,
    promptTagFormat: MEDIA_PROMPT_TAG_FORMATS.SPACED,
    followSourceAspectRatio: 'adaptive',
    fields: {
      videos: 'referenceVideos',
      images: 'referenceImages',
      audio: 'referenceAudios',
    },
  },
  unsupportedBehavior: 'warn',
};

// Scene3D supplies its proxy as the sole source video. The special endpoint
// transports that video and up to eight reference images, with no extra clips
// or audio conditioning.
const H3_SCENE3D_SOURCE_DURATION_MAX = 15;
const h3Scene3DToVideoReferences = {
  acceptedKinds: ['source-video', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS],
  acceptedMediaTypes: ['video', 'image'],
  roles: ['source-video', 'reference'],
  chips: ['source-video', 'reference'],
  limits: { total: 9, video: 1, image: 8 },
  constraints: {
    video: {
      requirePositiveDuration: true,
      requirePositiveDimensions: true,
      combinedDurationSeconds: { max: H3_SCENE3D_SOURCE_DURATION_MAX },
    },
  },
  transport: {
    strategy: ReferenceTransportStrategy.SOURCE_SLOT,
    // The proxy determines both output timing and aspect. This endpoint accepts neither input.
    sourceFraming: 'exact',
    fields: { source: 'sourceVideo', images: 'referenceImages' },
  },
  unsupportedBehavior: 'block',
};
// FLUX 3 keyframes are pinned by position in the clip, not by role, so the
// contract carries an ordered image list and no role vocabulary. The service
// spreads the ordered images across the clip's frames.
const flux3KeyframeReferences = {
  acceptedKinds: ['source', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS],
  acceptedMediaTypes: ['image'],
  roles: ['source', ...VIDEO_REFERENCE_ROLES],
  chips: ['reference'],
  limits: {
    total: 10,
    image: 10,
  },
  transport: {
    strategy: ReferenceTransportStrategy.FLAT,
    fields: { images: 'referenceImages' },
    // These images become FRAMES of the clip, not references held across it.
    // Element mentions must not route here; see ReferenceImageSemantics.
    imageSemantics: ReferenceImageSemantics.KEYFRAMES,
  },
  unsupportedBehavior: 'warn',
};

const geminiOmniReferenceImageReferences = {
  acceptedKinds: ['source', 'last-frame', ...SCENE_ASSET_REFERENCE_BUCKET_KINDS],
  acceptedMediaTypes: ['image'],
  roles: ['source', 'last-frame', ...VIDEO_REFERENCE_ROLES],
  chips: ['source', 'last-frame', ...VIDEO_REFERENCE_ROLES],
  limits: {
    total: 10,
    source: 1,
    'last-frame': 1,
    character: 4,
    prop: 3,
    brand: 1,
    environment: 2,
    style: 1,
    image: 10,
  },
  transport: {
    strategy: ReferenceTransportStrategy.PROMPT_TAGS,
    promptTagFormat: MEDIA_PROMPT_TAG_FORMATS.INDEXED_IMAGE,
    fields: { images: 'referenceImages' },
  },
  unsupportedBehavior: 'warn',
};

const sourceVideoReferences = ({
  referenceImages = false,
  referenceImageMax,
  keepAudio = false,
} = {}) => ({
  acceptedKinds: ['source-video', ...(referenceImages ? SCENE_ASSET_REFERENCE_BUCKET_KINDS : [])],
  acceptedMediaTypes: ['video', ...(referenceImages ? ['image'] : [])],
  roles: ['source-video', ...(referenceImages ? ['reference'] : [])],
  chips: ['source-video', ...(referenceImages ? ['reference'] : []), ...(keepAudio ? ['keep-audio'] : [])],
  limits: {
    total: referenceImages ? referenceImageMax + 1 : 1,
    video: 1,
    ...(referenceImages ? { image: referenceImageMax } : {}),
  },
  transport: {
    strategy: referenceImages
      ? ReferenceTransportStrategy.PROMPT_TAGS
      : ReferenceTransportStrategy.SOURCE_SLOT,
    fields: {
      source: 'sourceVideo',
      ...(referenceImages ? { images: 'referenceImages' } : {}),
    },
  },
  unsupportedBehavior: 'block',
});

function verification(endpoint, checkedAt = DEFAULT_CHECKED_AT) {
  return {
    source: 'curated-existing-config',
    provider: 'fal',
    endpoint,
    checkedAt,
    unmappedProviderParams: [],
  };
}

function videoFeatures({
  aspectRatios = VIDEO_ASPECT_RATIOS,
  resolutions = null,
  defaultResolution = null,
  durations = SEQUENCE_DURATIONS,
  // Models that CURATE their own `durations` must also declare the shortest of
  // them as the default — `videoModels.test.js` fails the build otherwise, so a
  // costly default can never be introduced silently.
  //
  // This fallback deliberately stays at 5 rather than deriving from `durations`.
  // A model that omits `durations` inherits the generic SEQUENCE_DURATIONS menu,
  // which is a placeholder rather than a verified capability list; deriving from
  // it would send providers a duration nobody confirmed they support (Kling would
  // drop 5s -> 3s on no evidence). Curate that model's real durations first, then
  // the guard applies to it too.
  defaultDuration = 5,
  durationSuffix = null,
  durationConstraints = null,
  generateAudio = false,
  // What the provider does when a caller does not say. Recorded so a take knows whether it was
  // made with sound; `null` where nobody has verified the provider's own default.
  generateAudioDefault = null,
  keepAudio = false,
  cameraMovement = true,
  lastFrame = null,
  sequence = null,
  nativeElements = null,
  referenceImages = null,
  referenceAudios = null,
  seed = false,
  acceleration = null,
  videoType = null,
  autoDownsample = null,
  audioSetting = null,
  sourceDuration = null,
  enhancePrompt = false,
} = {}) {
  return {
    aspectRatio: aspectRatios ? { options: aspectRatios, default: aspectRatios[0] } : null,
    resolution: resolutions ? { options: resolutions, default: defaultResolution || resolutions[0] } : null,
    duration: durations ? { options: durations, default: defaultDuration, suffix: durationSuffix } : null,
    durationConstraints,
    generateAudio: { enabled: generateAudio, default: generateAudioDefault },
    keepAudio: { enabled: keepAudio },
    cameraMovement: { enabled: cameraMovement },
    lastFrame,
    sequence,
    nativeElements,
    referenceImages,
    referenceAudios,
    seed: { enabled: seed },
    acceleration,
    videoType,
    autoDownsample,
    audioSetting,
    sourceDuration,
    enhancePrompt: { enabled: enhancePrompt },
  };
}

function route({
  endpoint,
  adapterId,
  serviceKey,
  serviceMethod,
  features = {},
  references = noReferences,
  timing: routeTiming = null,
  // A route may name itself when it does not share the model's version. This
  // builder drops anything it does not name, so an unlisted field is silently
  // discarded rather than rejected — which is how the first attempt at the
  // LTX extend label failed with no error anywhere.
  surfaceLabel = null,
}) {
  return {
    endpoint,
    features,
    references,
    timing: routeTiming,
    surfaceLabel,
    execution: {
      type: 'fal-service-method',
      adapterId,
      serviceKey,
      serviceMethod,
      endpoint,
    },
  };
}

function timing(baseSeconds, options = {}) {
  return {
    source: 'curated-existing-config',
    baseSeconds,
    ...options,
  };
}

export const VIDEO_MODEL_DEFINITIONS = [
  {
    id: 'ltx',
    label: 'LTX 2.5 Pro',
    retiredLabels: ['LTX Video 2.5'],
    family: ModelFamily.VIDEO,
    iconKey: 'lightricks',
    // fal list price: https://fal.ai/models/lightricks/ltx-2.5/text-to-video/pro
    pricing: { unit: 'second', usd: { min: 0.12, max: 0.17 }, asOf: '2026-10-20', note: '720p-1080p' },
    timing: timing(45, {
      baseDuration: 6,
      audioMult: 1.15,
      resolutions: { '720p': 0.85, '1080p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE, ModelSurface.VIDEO_EXTEND],
    topologies: [
      ModelTopology.TEXT_TO_VIDEO,
      ModelTopology.IMAGE_TO_VIDEO,
      ModelTopology.VIDEO_EXTEND,
    ],
    features: videoFeatures({
      aspectRatios: ['16:9', '9:16'],
      resolutions: ['720p', '1080p'],
      defaultResolution: '1080p',
      durations: [6, 8, 10],
      defaultDuration: 6,
      generateAudio: true,
      generateAudioDefault: true,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
    }),
    references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: LTX_ENDPOINTS.textToVideo,
        adapterId: 'video.text.single',
        serviceKey: 'ltxVideo',
        serviceMethod: 'generateLtxTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: LTX_ENDPOINTS.imageToVideo,
        adapterId: 'video.image.single',
        serviceKey: 'ltxVideo',
        serviceMethod: 'generateLtxImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_EXTEND]: route({
        endpoint: LTX_ENDPOINTS.extendVideo,
        // The extend lane's own name. It cannot derive from the model label
        // any more: generation moved to 2.5 and extend is pinned to 2.3, so
        // `${model.label} Extend` would announce a version this route does not
        // call. Lives here, beside the endpoint it describes.
        surfaceLabel: 'LTX Video 2.3 Extend',
        adapterId: 'video.extend.source-video',
        serviceKey: 'ltxVideo',
        serviceMethod: 'extendLtxVideo',
        references: sourceVideoReferences(),
        // Extend is its own capability contract: which end it can grow, how much,
        // and what it demands of the source. It lives on the route so eligibility
        // is a registry fact rather than an `if` in a component. LTX is the only
        // model that can grow the START of a clip, which is why the timeline
        // defaults to it — the drag gesture can produce a start-side zone.
        // Bounds match the live provider schema (min 2, max 20).
        features: {
          extend: {
            modes: ['start', 'end'],
            extendSeconds: { min: 2, max: 20 },
            maxSourceDurationSeconds: null,
            maxSourceBytes: null,
            requiresPrompt: false,
            generateAudio: false,
          },
        },
      }),
    },
    prompt: { enhancerId: 'video-ltx' },
    agent: { exposeAs: ['video-generator'] },
    verification: verification(LTX_ENDPOINTS.textToVideo),
  },
  {
    id: 'grokImagineVideo',
    label: 'Imagine 1.5',
    retiredLabels: ['Grok Imagine Video 1.5'],
    family: ModelFamily.VIDEO,
    iconKey: 'grok',
    pricing: { unit: 'second', usd: { min: 0.08, max: 0.14 }, asOf: '2026-06-21' },
    timing: timing(90, {
      baseDuration: 6,
      resolutions: { '480p': 0.75, '720p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.IMAGE_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['480p', '720p', '1080p'],
      defaultResolution: '720p',
      durations: [5, 6, 8, 10, 12, 15],
      defaultDuration: 5,
      cameraMovement: true,
    }),
    references: sourceImageReferences({ total: 1 }),
    routes: {
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'xai/grok-imagine-video/v1.5/image-to-video',
        adapterId: 'video.image.single',
        serviceKey: 'grokImagineVideo',
        serviceMethod: 'generateGrokImagineImageToVideo',
        references: sourceImageReferences({ total: 1 }),
      }),
    },
    prompt: { enhancerId: 'video-grok' },
    agent: { exposeAs: ['video-generator'] },
    verification: verification('xai/grok-imagine-video/v1.5/image-to-video'),
  },
  {
    id: 'klingO3',
    label: 'Kling O3',
    series: { id: 'kling', version: 'O3', rank: 1 },
    family: ModelFamily.VIDEO,
    iconKey: 'kling',
    pricing: { unit: 'second', usd: 0.17, asOf: '2026-04-25' },
    timing: timing(240, { baseDuration: 5, audioMult: 1.2 }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO],
    features: videoFeatures({
      generateAudio: true,
      generateAudioDefault: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      sequence: { strategy: 'multi-prompt', enabled: true },
      referenceImages: { max: 10 },
    }),
    references: orderedImageReferences({ total: 10 }),
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'fal-ai/kling-video/o3/standard/text-to-video',
        adapterId: 'video.text.sequence',
        serviceKey: 'klingO3',
        serviceMethod: 'generateKlingO3TextToVideo',
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'fal-ai/kling-video/o3/standard/image-to-video',
        adapterId: 'video.image.sequence',
        serviceKey: 'klingO3',
        serviceMethod: 'generateKlingO3ImageToVideo',
        references: {
          ...orderedImageReferences({ total: 10 }),
          limits: { ...orderedImageReferences({ total: 10 }).limits, source: 1, 'last-frame': 1 },
        },
      }),
    },
    prompt: { enhancerId: 'video-kling', sequenceEnhancerId: 'video-sequence-brief' },
    agent: { exposeAs: ['video-generator'] },
    verification: verification('fal-ai/kling-video/o3/standard/text-to-video'),
  },
  {
    id: 'klingV3',
    label: 'Kling V3',
    series: { id: 'kling', version: 'V3', rank: 2 },
    family: ModelFamily.VIDEO,
    iconKey: 'kling',
    pricing: { unit: 'second', usd: 0.25, asOf: '2026-04-25' },
    timing: timing(270, { baseDuration: 5, audioMult: 1.25 }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO],
    features: videoFeatures({
      generateAudio: true,
      generateAudioDefault: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      sequence: { strategy: 'multi-prompt', enabled: true },
      nativeElements: { enabled: true, max: 4 },
      referenceImages: { max: 10 },
    }),
    references: orderedImageReferences({
      total: 10,
      nativeElementsMax: 4,
      strategy: ReferenceTransportStrategy.NATIVE_ELEMENTS,
    }),
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'fal-ai/kling-video/v3/standard/text-to-video',
        adapterId: 'video.text.sequence',
        serviceKey: 'klingV3',
        serviceMethod: 'generateKlingV3TextToVideo',
        features: { nativeElements: { enabled: false, max: 0 } },
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'fal-ai/kling-video/v3/standard/image-to-video',
        adapterId: 'video.image.native-elements',
        serviceKey: 'klingV3',
        serviceMethod: 'generateKlingV3ImageToVideo',
        features: { nativeElements: { enabled: true, max: 4 } },
        references: orderedImageReferences({
          total: 10,
          nativeElementsMax: 4,
          strategy: ReferenceTransportStrategy.NATIVE_ELEMENTS,
        }),
      }),
    },
    prompt: { enhancerId: 'video-kling', sequenceEnhancerId: 'video-sequence-brief' },
    agent: { exposeAs: ['video-generator'] },
    verification: verification('fal-ai/kling-video/v3/standard/text-to-video'),
  },
  {
    id: 'geminiOmniFlash',
    label: 'Omni Flash',
    family: ModelFamily.VIDEO,
    iconKey: 'google',
    // fal list price: https://fal.ai/models/google/gemini-omni-flash/v1.1/edit
    pricing: { unit: 'second', usd: { min: 0.03, max: 0.3 }, asOf: '2026-10-20', note: '360p-4K' },
    timing: timing(120),
    surfaces: [ModelSurface.VIDEO_SMART_EDIT],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['360p', '720p', '1080p', '4k'],
      defaultResolution: '720p',
      durations: null,
      cameraMovement: false,
      referenceImages: { max: 0 },
    }),
    references: sourceVideoReferences(),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: GEMINI_OMNI_FLASH_ENDPOINTS.edit,
        adapterId: 'video.smart-edit.source-video',
        serviceKey: 'geminiOmniFlashVideoEdit',
        serviceMethod: 'generate',
        references: sourceVideoReferences(),
      }),
    },
    prompt: { enhancerId: 'video-edit-smart' },
    agent: { exposeAs: ['video-smart-edit'] },
    description: 'Conversational prompt-guided video editing with scene-coherence preservation.',
    ui: {
      smartEdit: {
        order: 0,
        modelName: 'Gemini Omni Flash Video Edit',
        loadingModelKey: 'gemini-omni-flash-video-edit',
        defaultParams: { resolution: '720p' },
      },
    },
    verification: verification(GEMINI_OMNI_FLASH_ENDPOINTS.edit),
  },
  {
    id: 'geminiOmniFlashVideo',
    label: 'Omni Flash',
    family: ModelFamily.VIDEO,
    iconKey: 'google',
    // fal list price: https://fal.ai/models/google/gemini-omni-flash/v1.1/text-to-video
    pricing: { unit: 'second', usd: { min: 0.03, max: 0.3 }, asOf: '2026-10-20', note: '360p-4K' },
    timing: timing(120, { baseDuration: 8 }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO, ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['16:9', '9:16'],
      resolutions: ['360p', '720p', '1080p', '4k'],
      defaultResolution: '720p',
      durations: [8],
      defaultDuration: 8,
      generateAudio: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      referenceImages: { max: 10 },
    }),
    references: geminiOmniReferenceImageReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: GEMINI_OMNI_FLASH_ENDPOINTS.textToVideo,
        adapterId: 'video.text.single',
        serviceKey: 'geminiOmniFlashVideoEdit',
        serviceMethod: 'generateTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: GEMINI_OMNI_FLASH_ENDPOINTS.imageToVideo,
        adapterId: 'video.image.single',
        serviceKey: 'geminiOmniFlashVideoEdit',
        serviceMethod: 'generateImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: GEMINI_OMNI_FLASH_ENDPOINTS.referenceToVideo,
        adapterId: 'video.reference-to-video.prompt-tags',
        serviceKey: 'geminiOmniFlashVideoEdit',
        serviceMethod: 'generateReferenceToVideo',
        references: geminiOmniReferenceImageReferences,
      }),
    },
    prompt: { enhancerId: 'video-gemini-omni' },
    agent: { exposeAs: ['video-generator'] },
    description: 'Fast Gemini video generation from text, one source image, or multiple image references.',
    verification: verification(GEMINI_OMNI_FLASH_ENDPOINTS.textToVideo),
  },
  {
    id: 'happyHorse',
    label: 'HappyHorse',
    family: ModelFamily.VIDEO,
    iconKey: 'fal',
    // fal list price: https://fal.ai/models/alibaba/happy-horse/video-edit (the page states both $0.14-$0.28/s and $0.28-$0.56/s; the range covers both)
    pricing: { unit: 'second', usd: { min: 0.14, max: 0.56 }, asOf: '2026-10-20', note: '720p-1080p; fal page conflicts' },
    timing: timing(180, {
      baseDuration: 5,
      resolutions: { '720p': 0.85, '1080p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_SMART_EDIT],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['720p', '1080p'],
      defaultResolution: '1080p',
      durations: null,
      cameraMovement: false,
      referenceImages: { max: 5 },
      audioSetting: { options: ['auto', 'origin'], default: 'auto' },
      sourceDuration: { min: 3, max: 60 },
    }),
    references: sourceVideoReferences({ referenceImages: true, referenceImageMax: 5 }),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'alibaba/happy-horse/video-edit',
        adapterId: 'video.smart-edit.source-video',
        serviceKey: 'happyHorseVideoEdit',
        serviceMethod: 'generate',
        references: sourceVideoReferences({ referenceImages: true, referenceImageMax: 5 }),
      }),
    },
    prompt: { enhancerId: 'video-edit-smart' },
    agent: { exposeAs: ['video-smart-edit'] },
    description: 'High-quality prompt-guided video editing with source-structure preservation and optional reference images.',
    ui: {
      smartEdit: {
        order: 1,
        modelName: 'HappyHorse Video Edit',
        loadingModelKey: 'happy-horse-video-edit',
        defaultParams: {
          resolution: '1080p',
          audioSetting: 'auto',
        },
      },
    },
    verification: verification('alibaba/happy-horse/video-edit'),
  },
  {
    id: 'wan27',
    label: 'Wan 2.7',
    family: ModelFamily.VIDEO,
    iconKey: 'fal',
    // fal list price: https://fal.ai/models/fal-ai/wan/v2.7/edit-video
    pricing: { unit: 'second', usd: { min: 0.1, max: 0.15 }, asOf: '2026-10-20', note: '720p-1080p' },
    timing: timing(120, {
      baseDuration: 5,
      resolutions: { '720p': 0.85, '1080p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_SMART_EDIT],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['auto', '16:9', '9:16', '1:1', '4:3', '3:4'],
      resolutions: ['720p', '1080p'],
      defaultResolution: '1080p',
      durations: [0, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      defaultDuration: 0,
      cameraMovement: false,
      referenceImages: { max: 1 },
      audioSetting: { options: ['auto', 'origin'], default: 'auto' },
      sourceDuration: { min: 2, max: 10 },
    }),
    references: sourceVideoReferences({ referenceImages: true, referenceImageMax: 1 }),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'fal-ai/wan/v2.7/edit-video',
        adapterId: 'video.smart-edit.source-video',
        serviceKey: 'wan27EditVideo',
        serviceMethod: 'generate',
        references: sourceVideoReferences({ referenceImages: true, referenceImageMax: 1 }),
      }),
    },
    prompt: { enhancerId: 'video-edit-smart' },
    agent: { exposeAs: ['video-smart-edit'] },
    description: 'Prompt-guided short-clip editing with strong motion coherence and optional reference image control.',
    ui: {
      smartEdit: {
        order: 2,
        modelName: 'Wan 2.7 Edit Video',
        loadingModelKey: 'wan-2-7-edit-video',
        defaultParams: {
          resolution: '1080p',
          duration: 0,
          audioSetting: 'auto',
        },
      },
    },
    verification: verification('fal-ai/wan/v2.7/edit-video'),
  },
  {
    id: 'seedance',
    label: 'Seedance 2.5',
    series: { id: 'seedance', version: '2.5', rank: 2 },
    family: ModelFamily.VIDEO,
    iconKey: 'bytedance',
    pricing: { unit: 'second', usd: 0.12, asOf: '2026-04-25' },
    timing: timing(120, {
      baseDuration: 5,
      audioMult: 1.1,
      resolutions: { '480p': 0.8, '720p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE, ModelSurface.VIDEO_RESTYLE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO, ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['auto', '16:9', '9:16', '1:1', '21:9', '4:3', '3:4'],
      resolutions: SEEDANCE_REFERENCE_ENDPOINT.resolutions,
      defaultResolution: '720p',
      durations: SEEDANCE_REFERENCE_ENDPOINT.durations,
      defaultDuration: 4,
      generateAudio: true,
      generateAudioDefault: true,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      sequence: { strategy: 'prompt-collation', enabled: true },
      referenceImages: { max: SEEDANCE_REFERENCE_ENDPOINT.limits.image },
      referenceAudios: { max: SEEDANCE_REFERENCE_ENDPOINT.limits.audio },
      seed: true,
    }),
    references: seedance25ReferenceVideoReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: SEEDANCE_REFERENCE_ENDPOINT.textToVideoModelId,
        adapterId: 'video.text.single',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: SEEDANCE_REFERENCE_ENDPOINT.imageToVideoModelId,
        adapterId: 'video.image.single',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: SEEDANCE_REFERENCE_ENDPOINT.modelId,
        adapterId: 'video.reference-to-video.prompt-tags',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceReferenceToVideo',
        references: seedance25ReferenceVideoReferences,
        timing: timing(150, {
          baseDuration: 5,
          audioMult: 1.1,
          resolutions: { '480p': 0.8, '720p': 1 },
        }),
      }),
    },
    prompt: {
      enhancerId: 'video-seedance',
      restyleEnhancerId: 'video-restyle-seedance',
    },
    agent: { exposeAs: ['video-generator', 'video-restyle'] },
    description: 'Reference-driven video generation and restyling with optional image guides and generated audio.',
    ui: {
      restyle: {
        order: 3,
      },
    },
    verification: verification(SEEDANCE_REFERENCE_ENDPOINT.textToVideoModelId, '2026-09-25'),
  },
  {
    id: 'seedanceDraft',
    label: 'Seedance 2.5 Draft',
    series: { id: 'seedance', version: '2.5 Draft', rank: 1 },
    family: ModelFamily.VIDEO,
    iconKey: 'bytedance',
    pricing: { unit: 'second', usd: 0.2205, asOf: '2026-09-26', note: 'preview' },
    timing: timing(75, {
      baseDuration: 5,
      audioMult: 1.05,
      resolutions: { '480p': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE, ModelSurface.VIDEO_RESTYLE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO, ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['auto', '16:9', '9:16', '1:1', '21:9', '4:3', '3:4'],
      resolutions: SEEDANCE_DRAFT_ENDPOINT.resolutions,
      defaultResolution: '480p',
      durations: SEEDANCE_DRAFT_ENDPOINT.durations,
      defaultDuration: 4,
      generateAudio: true,
      generateAudioDefault: true,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      sequence: { strategy: 'prompt-collation', enabled: true },
      referenceImages: { max: SEEDANCE_DRAFT_ENDPOINT.limits.image },
      referenceAudios: { max: SEEDANCE_DRAFT_ENDPOINT.limits.audio },
      seed: true,
    }),
    references: seedanceDraftReferenceVideoReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: SEEDANCE_DRAFT_ENDPOINT.textToVideoModelId,
        adapterId: 'video.text.single',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceDraftTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: SEEDANCE_DRAFT_ENDPOINT.imageToVideoModelId,
        adapterId: 'video.image.single',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceDraftImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: SEEDANCE_DRAFT_ENDPOINT.modelId,
        adapterId: 'video.reference-to-video.prompt-tags',
        serviceKey: 'seedanceVideo',
        serviceMethod: 'generateSeedanceDraftReferenceToVideo',
        references: seedanceDraftReferenceVideoReferences,
        timing: timing(90, {
          baseDuration: 5,
          audioMult: 1.05,
          resolutions: { '480p': 1 },
        }),
      }),
    },
    prompt: {
      enhancerId: 'video-seedance',
      restyleEnhancerId: 'video-restyle-seedance',
    },
    agent: { exposeAs: ['video-generator', 'video-restyle'] },
    description: 'Fast 480p Seedance 2.5 preview that can be completed at 1080p within seven days.',
    ui: {
      restyle: {
        order: 4,
      },
    },
    verification: verification(SEEDANCE_DRAFT_ENDPOINT.completionModelId, '2026-09-25'),
  },
  {
    id: 'veo31',
    label: 'Veo 3.1',
    family: ModelFamily.VIDEO,
    iconKey: 'google',
    pricing: { unit: 'second', usd: 0.2, asOf: '2026-04-25' },
    timing: timing(95, {
      baseDuration: 8,
      audioMult: 1.35,
      resolutions: { '720p': 1, '1080p': 1.45, '4k': 2.8 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['16:9', '9:16'],
      resolutions: ['720p', '1080p', '4k'],
      defaultResolution: '720p',
      durations: [4, 6, 8],
      defaultDuration: 4,
      durationSuffix: 's',
      generateAudio: true,
      generateAudioDefault: true,
      lastFrame: {
        enabled: true,
        paramName: 'last_frame_url',
        endpoint: 'fal-ai/veo3.1/first-last-frame-to-video',
        firstFrameParamName: 'first_frame_url',
      },
    }),
    references: sourceImageReferences({ lastFrameField: 'last_frame_url', total: 2 }),
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'fal-ai/veo3.1',
        adapterId: 'video.text.single',
        serviceKey: 'veo31',
        serviceMethod: 'generateVeo31TextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'fal-ai/veo3.1/image-to-video',
        adapterId: 'video.image.first-last-frame',
        serviceKey: 'veo31',
        serviceMethod: 'generateVeo31ImageToVideo',
      }),
    },
    prompt: { enhancerId: 'video-veo31' },
    agent: { exposeAs: ['video-generator'] },
    verification: verification('fal-ai/veo3.1'),
  },
  {
    id: 'lucyRestyle',
    label: 'Lucy Restyle',
    family: ModelFamily.VIDEO,
    iconKey: 'generic-ai',
    pricing: { unit: 'second', usd: 0.01, asOf: '2026-04-25' },
    timing: timing(45, { baseDuration: 5 }),
    surfaces: [ModelSurface.VIDEO_RESTYLE],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['720p'],
      defaultResolution: '720p',
      durations: null,
      cameraMovement: false,
      seed: true,
      sourceDuration: { max: 600 },
      enhancePrompt: true,
    }),
    references: sourceVideoReferences(),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'decart/lucy-restyle',
        adapterId: 'video.restyle.source-video',
        serviceKey: 'lucyRestyle',
        serviceMethod: 'generateLucyRestyle',
        references: sourceVideoReferences(),
      }),
    },
    prompt: { enhancerId: 'video-restyle-lucy' },
    agent: { exposeAs: ['video-restyle'], defaultFor: ['video-restyle'] },
    description: 'Long-form restyling up to 10 min. 720p. Best value.',
    ui: {
      restyle: {
        order: 0,
      },
    },
    verification: verification('decart/lucy-restyle'),
  },
  {
    id: 'klingO1Edit',
    label: 'Kling O3 Edit',
    family: ModelFamily.VIDEO,
    iconKey: 'kling',
    pricing: { unit: 'second', usd: 0.168, asOf: '2026-04-25' },
    timing: timing(240, { baseDuration: 5 }),
    surfaces: [ModelSurface.VIDEO_RESTYLE],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['up to 4K'],
      durations: null,
      keepAudio: true,
      cameraMovement: false,
      referenceImages: { max: 3 },
      sourceDuration: { min: 3, max: 10 },
    }),
    references: sourceVideoReferences({
      referenceImages: true,
      referenceImageMax: 3,
      keepAudio: true,
    }),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: KLING_O1_EDIT_ENDPOINTS.videoToVideo,
        adapterId: 'video.restyle.references',
        serviceKey: 'klingO1Edit',
        serviceMethod: 'generateKlingO1Edit',
        references: sourceVideoReferences({
          referenceImages: true,
          referenceImageMax: 3,
          keepAudio: true,
        }),
      }),
    },
    prompt: { enhancerId: 'video-restyle-kling-o1' },
    agent: { exposeAs: ['video-restyle'] },
    description: 'Premium quality, 3-10s clips. Style reference images supported.',
    ui: {
      restyle: {
        order: 1,
      },
    },
    verification: verification(KLING_O1_EDIT_ENDPOINTS.videoToVideo),
  },
  {
    id: 'minimaxH3Max3DToVideo',
    label: 'MiniMax H3 Max 3D to Video',
    series: { id: 'minimax', version: 'H3 Max 3D to Video', rank: -1 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    // https://fal.ai/models/minimax/h3-max/3d-to-video — video rate plus $0.50/request, input tokens and generated refs.
    pricing: { unit: 'second', usd: { min: 0.05, max: 0.16 }, asOf: '2026-10-21', note: '5s billing min; +$0.50 + inputs/refs' },
    timing: timing(120, { baseDuration: 5, resolutions: { '480P': 0.7, '768P': 1 } }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['480P', '768P', '1080P'],
      defaultResolution: '768P',
      // The endpoint has no duration input: its source video determines output timing.
      durations: null,
      sourceDuration: { max: H3_SCENE3D_SOURCE_DURATION_MAX },
      generateAudio: false,
      referenceImages: { max: 8 },
    }),
    references: h3Scene3DToVideoReferences,
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.threeDToVideo,
        adapterId: 'video.scene-3d-to-video',
        serviceKey: 'minimaxH3SpecialVideo',
        serviceMethod: 'generateH3Max3DToVideo',
        references: h3Scene3DToVideoReferences,
      }),
    },
    prompt: { enhancerId: 'video-minimax-h3' },
    agent: { exposeAs: [] },
    description: 'Turns the Scene3D proxy into a photorealistic clip while preserving its camera, layout and timing.',
    ui: { generate: { hostReferenceKinds: ['scene3d'] } },
    verification: verification(MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.threeDToVideo, '2026-10-19'),
  },
  {
    id: 'minimaxH3MaxRecast',
    label: 'MiniMax H3 Max Recast',
    series: { id: 'minimax', version: 'H3 Max Recast', rank: -2 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    // https://fal.ai/models/minimax/h3-max/recast — reference images included.
    pricing: { unit: 'second', usd: { min: 0.30, max: 0.45 }, asOf: '2026-10-21', note: '768p-1080p' },
    timing: timing(150, { baseDuration: 5, resolutions: { '768P': 1, '1080P': 1.3 } }),
    surfaces: [ModelSurface.VIDEO_RECAST],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: null,
      resolutions: ['768P', '1080P'],
      // The provider defaults to 1080P; the cheaper tier is the repo default.
      defaultResolution: '768P',
      durations: null,
      cameraMovement: false,
      // One performer photo per person, cast onto the main people left to right.
      referenceImages: { max: 4 },
      sourceDuration: { min: 5, max: 30 },
    }),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.recast,
        adapterId: 'video.recast.source-video',
        serviceKey: 'minimaxH3SpecialVideo',
        serviceMethod: 'generateH3MaxRecast',
        references: noReferences,
      }),
    },
    prompt: { enhancerId: 'video-recast' },
    agent: { exposeAs: [] },
    description: 'Recasts the main people in a source video from ordered performer photos while keeping its motion, camera and soundtrack.',
    verification: verification(MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.recast, '2026-10-20'),
  },
  {
    id: 'minimaxH3MaxRelight',
    label: 'MiniMax H3 Max Relight',
    series: { id: 'minimax', version: 'H3 Max Relight', rank: 0 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    // https://fal.ai/models/minimax/h3-max/relight
    pricing: { unit: 'second', usd: { min: 0.05, max: 0.32 }, asOf: '2026-10-21', note: '480p-2K' },
    timing: timing(120, { baseDuration: 5, resolutions: { '480P': 0.7, '768P': 1 } }),
    surfaces: [ModelSurface.VIDEO_RELIGHT],
    topologies: [ModelTopology.VIDEO_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['16:9', '21:9', '4:3', '1:1', '3:4', '9:16'],
      resolutions: ['480P', '768P', '1080P', '2K'],
      defaultResolution: '768P',
      durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      defaultDuration: 5,
      generateAudio: false,
      sourceDuration: { max: 15 },
    }),
    routes: {
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.relight,
        adapterId: 'video.relight.source-video',
        serviceKey: 'minimaxH3SpecialVideo',
        serviceMethod: 'generateH3MaxRelight',
        references: noReferences,
      }),
    },
    prompt: { enhancerId: 'video-minimax-h3' },
    agent: { exposeAs: [] },
    description: 'Relights a source video from a lighting-sphere reference while preserving motion, camera and audio.',
    verification: verification(MINIMAX_H3_MAX_SPECIAL_ENDPOINTS.relight, '2026-10-19'),
  },
  {
    id: 'minimaxH3',
    label: 'MiniMax H3',
    series: { id: 'minimax', version: 'H3', rank: 3 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    pricing: { unit: 'second', usd: 0.06, asOf: '2026-08-25', note: '768P' },
    timing: timing(180, {
      baseDuration: 5,
      resolutions: { '768P': 0.7, '2K': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE, ModelSurface.VIDEO_RESTYLE],
    topologies: [
      ModelTopology.TEXT_TO_VIDEO,
      ModelTopology.IMAGE_TO_VIDEO,
      ModelTopology.VIDEO_TO_VIDEO,
    ],
    features: videoFeatures({
      aspectRatios: ['16:9', '21:9', '4:3', '1:1', '3:4', '9:16'],
      resolutions: ['480P', '768P', '2K', '4K'],
      // The provider defaults to 2K. We default to the cheaper tier for the
      // same reason the duration guard exists: cost is opt-in, not automatic.
      defaultResolution: '768P',
      durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      defaultDuration: 5,
      generateAudio: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      // Per-type provider caps, not the combined total. Declaring 12 here
      // advertised twelve image slots on a route that rejects the tenth.
      referenceImages: { max: 9 },
      referenceAudios: { max: 3 },
    }),
    references: h3ReferenceVideoReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'minimax/h3/text-to-video',
        adapterId: 'video.text.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3TextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'minimax/h3/image-to-video',
        adapterId: 'video.image.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3ImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'minimax/h3/reference-to-video',
        adapterId: 'video.reference-to-video.prompt-tags',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3ReferenceToVideo',
        references: h3ReferenceVideoReferences,
      }),
    },
    prompt: {
      enhancerId: 'video-minimax-h3',
      restyleEnhancerId: 'video-minimax-h3',
    },
    agent: { exposeAs: ['video-generator', 'video-restyle'] },
    description: 'Multimodal video generation from text, a still frame, or mixed image, clip and audio references.',
    ui: {
      restyle: {
        order: 5,
      },
    },
    verification: verification('minimax/h3/text-to-video'),
  },
  {
    id: 'minimaxH3Max',
    label: 'MiniMax H3 Max',
    series: { id: 'minimax', version: 'H3 Max', rank: 2 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    pricing: { unit: 'second', usd: 0.08, asOf: '2026-08-25', note: '768P' },
    timing: timing(120, {
      baseDuration: 5,
      resolutions: { '480P': 0.7, '768P': 1 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [
      ModelTopology.TEXT_TO_VIDEO,
      ModelTopology.IMAGE_TO_VIDEO,
      ModelTopology.VIDEO_TO_VIDEO,
    ],
    features: videoFeatures({
      aspectRatios: ['16:9', '21:9', '4:3', '1:1', '3:4', '9:16'],
      resolutions: ['480P', '768P', '1080P'],
      defaultResolution: '768P',
      durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      defaultDuration: 5,
      generateAudio: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
      referenceImages: { max: 9 },
      referenceAudios: { max: 3 },
    }),
    references: h3ReferenceVideoReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'minimax/h3-max/text-to-video',
        adapterId: 'video.text.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3MaxTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'minimax/h3-max/image-to-video',
        adapterId: 'video.image.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3MaxImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'minimax/h3-max/reference-to-video',
        adapterId: 'video.reference-to-video.prompt-tags',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3MaxReferenceToVideo',
        references: h3ReferenceVideoReferences,
      }),
    },
    // Same family, same prompt behavior. A forked enhancer entry would drift
    // from H3's without ever being told to.
    prompt: { enhancerId: 'video-minimax-h3' },
    agent: { exposeAs: ['video-generator'], defaultFor: ['video-generator'] },
    description: 'Post-trained MiniMax H3 variant tuned for prompt adherence and throughput, from text or a still frame.',
    verification: verification('minimax/h3-max/text-to-video'),
  },
  {
    id: 'minimaxH3MaxTurbo',
    label: 'MiniMax H3 Max Turbo',
    series: { id: 'minimax', version: 'H3 Max Turbo', rank: 1 },
    family: ModelFamily.VIDEO,
    iconKey: 'minimax',
    // fal list price: https://fal.ai/models/minimax/h3-max-turbo/text-to-video (35%% promo until Oct 31: $0.01625-$0.052/s; list after: $0.025-$0.08/s)
    pricing: { unit: 'second', usd: { min: 0.01625, max: 0.08 }, asOf: '2026-10-20', note: '480p-1080p; promo to Oct 31' },
    timing: timing(75, {
      baseDuration: 5,
      resolutions: { '480P': 0.7, '768P': 1, '1080P': 1.35 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE],
    topologies: [ModelTopology.TEXT_TO_VIDEO, ModelTopology.IMAGE_TO_VIDEO],
    features: videoFeatures({
      aspectRatios: ['16:9', '21:9', '4:3', '1:1', '3:4', '9:16'],
      resolutions: ['480P', '768P', '1080P'],
      defaultResolution: '768P',
      durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      defaultDuration: 5,
      generateAudio: false,
      lastFrame: { enabled: true, paramName: 'end_image_url' },
    }),
    references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'minimax/h3-max-turbo/text-to-video',
        adapterId: 'video.text.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3MaxTurboTextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'minimax/h3-max-turbo/image-to-video',
        adapterId: 'video.image.single',
        serviceKey: 'minimaxH3Video',
        serviceMethod: 'generateH3MaxTurboImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
    },
    prompt: { enhancerId: 'video-minimax-h3' },
    agent: { exposeAs: ['video-generator'] },
    description: 'Higher-throughput H3 Max tier for text and first/last-frame video generation.',
    verification: verification('minimax/h3-max-turbo/text-to-video', '2026-09-25'),
  },
  {
    id: 'flux3',
    label: 'FLUX 3',
    family: ModelFamily.VIDEO,
    iconKey: 'flux',
    // fal list price: https://fal.ai/models/blackforestlabs/flux-3/text-to-video
    pricing: { unit: 'second', usd: { min: 0.17, max: 0.47 }, asOf: '2026-10-20', note: '720p-4K' },
    timing: timing(120, {
      baseDuration: 5,
      audioMult: 1.15,
      resolutions: { '720p': 1, '1080p': 1.4 },
    }),
    surfaces: [ModelSurface.VIDEO_GENERATE, ModelSurface.VIDEO_EXTEND],
    topologies: [
      ModelTopology.TEXT_TO_VIDEO,
      ModelTopology.IMAGE_TO_VIDEO,
      ModelTopology.VIDEO_TO_VIDEO,
      ModelTopology.VIDEO_EXTEND,
    ],
    features: videoFeatures({
      aspectRatios: ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'],
      resolutions: ['720p', '1080p'],
      defaultResolution: '720p',
      durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
      defaultDuration: 5,
      generateAudio: true,
      generateAudioDefault: false,
      lastFrame: {
        enabled: true,
        paramName: 'end_image_url',
        endpoint: 'blackforestlabs/flux-3/first-last-frame-to-video',
        firstFrameParamName: 'start_image_url',
      },
      referenceImages: { max: 10 },
    }),
    references: flux3KeyframeReferences,
    routes: {
      [ModelTopology.TEXT_TO_VIDEO]: route({
        endpoint: 'blackforestlabs/flux-3/text-to-video',
        adapterId: 'video.text.single',
        serviceKey: 'flux3Video',
        serviceMethod: 'generateFlux3TextToVideo',
        references: noReferences,
      }),
      [ModelTopology.IMAGE_TO_VIDEO]: route({
        endpoint: 'blackforestlabs/flux-3/image-to-video',
        adapterId: 'video.image.first-last-frame',
        serviceKey: 'flux3Video',
        serviceMethod: 'generateFlux3ImageToVideo',
        references: sourceImageReferences({ lastFrameField: 'end_image_url', total: 2 }),
      }),
      [ModelTopology.VIDEO_TO_VIDEO]: route({
        endpoint: 'blackforestlabs/flux-3/keyframes-to-video',
        adapterId: 'video.image.sequence',
        serviceKey: 'flux3Video',
        serviceMethod: 'generateFlux3KeyframesToVideo',
        references: flux3KeyframeReferences,
      }),
      [ModelTopology.VIDEO_EXTEND]: route({
        endpoint: 'blackforestlabs/flux-3/extend-video',
        adapterId: 'video.extend.source-video',
        serviceKey: 'flux3Video',
        serviceMethod: 'generateFlux3ExtendVideo',
        references: sourceVideoReferences(),
        // FLUX 3 grows a clip from its FINAL frames and has no mode parameter,
        // so it cannot prepend — hence end-only, where LTX does both. Its source
        // caps and 5s floor are the provider's, and are what the picker greys
        // the model out on.
        features: {
          extend: {
            modes: ['end'],
            extendSeconds: { min: 5, max: 20 },
            maxSourceDurationSeconds: 15,
            maxSourceBytes: 50 * 1024 * 1024,
            requiresPrompt: true,
            generateAudio: true,
          },
        },
      }),
    },
    prompt: { enhancerId: 'video-flux3' },
    agent: { exposeAs: ['video-generator'] },
    description: 'Long-form video from text, a still frame, a first and last frame, or a run of pinned keyframes.',
    verification: {
      ...verification('blackforestlabs/flux-3/text-to-video'),
      unmappedProviderParams: ['safety_tolerance'],
    },
  },
];
